package fr.frux.feature.map

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Tune
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxFilterChip
import fr.frux.core.designsystem.components.FruxModalBottomSheet
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_filters_button_description
import fr.frux.feature.map.resources.map_filters_button_description_active
import fr.frux.feature.map.resources.map_filters_chip_harvest_part_feuille
import fr.frux.feature.map.resources.map_filters_chip_harvest_part_fleur
import fr.frux.feature.map.resources.map_filters_chip_harvest_part_fruit
import fr.frux.feature.map.resources.map_filters_chip_harvest_part_noix
import fr.frux.feature.map.resources.map_filters_chip_harvest_part_racine
import fr.frux.feature.map.resources.map_filters_chip_hide_needs_check
import fr.frux.feature.map.resources.map_filters_chip_in_season
import fr.frux.feature.map.resources.map_filters_chip_plant_form_arbre
import fr.frux.feature.map.resources.map_filters_chip_plant_form_arbuste
import fr.frux.feature.map.resources.map_filters_chip_plant_form_herbacee
import fr.frux.feature.map.resources.map_filters_chip_plant_form_liane
import fr.frux.feature.map.resources.map_filters_clear_all
import fr.frux.feature.map.resources.map_filters_section_harvest_part
import fr.frux.feature.map.resources.map_filters_section_identification
import fr.frux.feature.map.resources.map_filters_section_plant_form
import fr.frux.feature.map.resources.map_filters_section_season
import fr.frux.feature.map.resources.map_filters_section_season_highlights
import fr.frux.feature.map.resources.map_filters_sheet_description
import fr.frux.feature.map.resources.map_filters_title
import fr.frux.shared.domain.model.HarvestPart
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.PlantForm
import fr.frux.shared.domain.model.Species
import org.jetbrains.compose.resources.stringResource

/** Ordre produit des parties recoltees : jamais les noms bruts des enums a l'ecran. */
private val HARVEST_PART_CHIP_ORDER =
    listOf(
        HarvestPart.FRUIT,
        HarvestPart.NOIX,
        HarvestPart.FLEUR,
        HarvestPart.FEUILLE,
        HarvestPart.RACINE,
    )

/** La forme reste un axe secondaire, apres la partie recoltee dans la rangee. */
private val PLANT_FORM_CHIP_ORDER =
    listOf(
        PlantForm.ARBRE,
        PlantForm.ARBUSTE,
        PlantForm.HERBACEE,
        PlantForm.LIANE,
    )

@Composable
private fun HarvestPart.chipLabel(): String =
    when (this) {
        HarvestPart.FRUIT -> stringResource(Res.string.map_filters_chip_harvest_part_fruit)
        HarvestPart.NOIX -> stringResource(Res.string.map_filters_chip_harvest_part_noix)
        HarvestPart.FLEUR -> stringResource(Res.string.map_filters_chip_harvest_part_fleur)
        HarvestPart.FEUILLE -> stringResource(Res.string.map_filters_chip_harvest_part_feuille)
        HarvestPart.RACINE -> stringResource(Res.string.map_filters_chip_harvest_part_racine)
    }

@Composable
private fun PlantForm.chipLabel(): String =
    when (this) {
        PlantForm.ARBRE -> stringResource(Res.string.map_filters_chip_plant_form_arbre)
        PlantForm.ARBUSTE -> stringResource(Res.string.map_filters_chip_plant_form_arbuste)
        PlantForm.HERBACEE -> stringResource(Res.string.map_filters_chip_plant_form_herbacee)
        PlantForm.LIANE -> stringResource(Res.string.map_filters_chip_plant_form_liane)
    }

/**
 * Entree unique vers les filtres (#262), a droite de la barre de recherche. Remplace la rangee de
 * chips qui doublait la barre sur toute la largeur : les filtres deviennent invisibles au repos,
 * donc leur ACTIVITE doit rester visible — d'ou la pastille d'angle, seul indice qu'un filtre
 * restreint ce que la carte montre.
 *
 * Cercle et elevation des controles carte ([LocateMeButton]) plutot que la pilule des chips :
 * cette entree ouvre une feuille, elle ne porte pas d'etat selectionne a l'ecran.
 */
@Composable
internal fun FilterButton(
    hasActiveFilter: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        modifier = modifier.size(FILTER_BUTTON_SIZE),
        shape = CircleShape,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.primary,
        shadowElevation = 2.dp,
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(
                imageVector = Icons.Outlined.Tune,
                // Porte l'etat actif : un lecteur d'ecran ne voit pas la pastille d'angle, qui est
                // purement decorative.
                contentDescription =
                    if (hasActiveFilter) {
                        stringResource(Res.string.map_filters_button_description_active)
                    } else {
                        stringResource(Res.string.map_filters_button_description)
                    },
            )
            if (hasActiveFilter) {
                Box(
                    modifier =
                        Modifier
                            .align(Alignment.TopEnd)
                            .padding(top = FILTER_BADGE_INSET, end = FILTER_BADGE_INSET)
                            .size(FILTER_BADGE_SIZE)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primary),
                )
            }
        }
    }
}

/** Empreinte du bouton « Filtrer » : celle de la pastille de recherche qu'il borde. */
private val FILTER_BUTTON_SIZE = 48.dp
private val FILTER_BADGE_SIZE = 10.dp
private val FILTER_BADGE_INSET = 10.dp

/**
 * Feuille de filtres (#262), ouverte par [FilterButton]. Modale, comme la legende : contrairement
 * aux feuilles du slot bas, elle n'accompagne pas la lecture de la carte — on choisit, puis on
 * revient.
 *
 * « Tous » n'est plus une option : l'ancien chip donnait le poids visuel maximal (vert plein,
 * coche) a l'ABSENCE de filtre. Ici l'etat neutre est simplement « rien de coche », et
 * « Tout effacer » n'apparait que s'il y a quelque chose a effacer.
 *
 * Partie et forme restent a choix unique, comme les chips qu'elles remplacent : c'est ce que
 * [MapFilter] sait porter, et l'API n'accepte qu'une valeur par axe.
 *
 * Section « De saison en ce moment » : affiche les especes referencees comme cueillables le mois
 * courant, permutant la selection avec « null » pour deselectionner.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun MapFiltersSheet(
    filter: MapFilter,
    hasSpeciesFilter: Boolean,
    seasonHighlights: List<Species>,
    selectedSpeciesRollupId: Long?,
    onSpeciesSelected: (Species?) -> Unit,
    onFilterChange: (MapFilter) -> Unit,
    onClearFilters: () -> Unit,
    onDismiss: () -> Unit,
) {
    FruxModalBottomSheet(onDismiss = onDismiss, skipPartiallyExpanded = true) {
        val sheetDescription = stringResource(Res.string.map_filters_sheet_description)
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = FruxSpacing.md)
                    .padding(bottom = FruxSpacing.lg)
                    .semantics { contentDescription = sheetDescription },
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = stringResource(Res.string.map_filters_title),
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.weight(1f),
                )
                // Une seule sortie de secours, et seulement quand elle a un effet : proposer
                // « Tout effacer » sur une feuille vierge redonnerait a l'etat neutre le poids
                // que le chip « Tous » avait de trop.
                if (filter.isActive || hasSpeciesFilter) {
                    TextButton(
                        onClick = {
                            onClearFilters()
                            onDismiss()
                        },
                    ) {
                        Text(text = stringResource(Res.string.map_filters_clear_all))
                    }
                }
            }

            FiltersSection(label = stringResource(Res.string.map_filters_section_season)) {
                FruxFilterChip(
                    label = stringResource(Res.string.map_filters_chip_in_season),
                    selected = filter.inSeason,
                    onClick = { onFilterChange(filter.copy(inSeason = !filter.inSeason)) },
                )
            }

            if (seasonHighlights.isNotEmpty()) {
                FiltersSection(label = stringResource(Res.string.map_filters_section_season_highlights)) {
                    seasonHighlights.forEach { species ->
                        val selected = selectedSpeciesRollupId == species.rollupId
                        FruxFilterChip(
                            label = species.commonNameFr,
                            selected = selected,
                            onClick = { onSpeciesSelected(if (selected) null else species) },
                        )
                    }
                }
            }

            FiltersSection(label = stringResource(Res.string.map_filters_section_harvest_part)) {
                HARVEST_PART_CHIP_ORDER.forEach { part ->
                    FruxFilterChip(
                        label = part.chipLabel(),
                        selected = filter.part == part,
                        onClick = {
                            onFilterChange(filter.copy(part = if (filter.part == part) null else part))
                        },
                    )
                }
            }

            FiltersSection(label = stringResource(Res.string.map_filters_section_plant_form)) {
                PLANT_FORM_CHIP_ORDER.forEach { form ->
                    FruxFilterChip(
                        label = form.chipLabel(),
                        selected = filter.form == form,
                        onClick = {
                            onFilterChange(filter.copy(form = if (filter.form == form) null else form))
                        },
                    )
                }
            }

            // Formule en soustraction (#265) : « voir seulement les douteux » construirait une
            // carte du danger, ce que l'ADR 0015 refuse.
            FiltersSection(label = stringResource(Res.string.map_filters_section_identification)) {
                FruxFilterChip(
                    label = stringResource(Res.string.map_filters_chip_hide_needs_check),
                    selected = filter.hideNeedsCheck,
                    onClick = { onFilterChange(filter.copy(hideNeedsCheck = !filter.hideNeedsCheck)) },
                )
            }
        }
    }
}

/**
 * Un axe de filtre : son intitule, puis ses chips qui passent a la ligne. `FlowRow` plutot qu'une
 * `LazyRow` : dans une feuille rien n'est hors cadre, et c'est precisement le defilement coupe net
 * a droite qui rendait l'ancienne rangee illisible (#262).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun FiltersSection(
    label: String,
    content: @Composable FlowRowScope.() -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
            content = content,
        )
    }
}
